import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { ColumnConfig } from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import {
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { ResourceTableView } from '@Components/ResourceTable';
import { useResourceTableController } from '@Components/ResourceTable/useResourceTableController';
import type { ToastGuid } from '@Types/toastGuid';
import { buildFilterParams } from '@Utils/buildFilterParams';
import { useToastGuids } from '@Hooks/useToastGuids';
import { useDeleteToastGuid } from '@Hooks/useDeleteToastGuid';
import { CopyableText } from '@Components/CopyableText';
import { Hash, Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useVenues } from '@Hooks/useVenues';
import ToastGuidSheet from './ToastGuidSheet';
import ConfirmationDialog from '@Components/ConfirmationDialog';

const TOAST_GUID_FIELD_MAPPING: Record<string, string> = {
  name: 'filter[name]',
  guid: 'filter[guid]',
  venue: 'filter[venueId]',
};

export default function ToastGuidList() {
  const venues = useVenues({ pageSize: -1 });

  const venueMap = useMemo(() => {
    return new Map((venues.data ?? []).map((v) => [v.id, v.name]));
  }, [venues.data]);

  const toastGuidColumnConfigs: ColumnConfig<ToastGuid>[] = useMemo(
    () => [
      {
        id: 'name',
        headerLabel: 'Name',
        accessor: (row) => row.name ?? '-',
        type: 'text',
        size: 200,
      },
      {
        id: 'guid',
        headerLabel: 'GUID',
        accessor: (row) => row.guid,
        type: 'text',
        size: 350,
      },
      {
        id: 'venue',
        headerLabel: 'Venue',
        accessor: (row) => venueMap.get(row.venueId) ?? row.venueId,
        type: 'text',
        size: 250,
        cell: (info) => {
          const row = info.row.original;
          const displayName = venueMap.get(row.venueId) ?? row.venueId;
          return (
            <CopyableText
              text={displayName}
              tooltipContent={row.venueId}
              copyValue={row.venueId}
            />
          );
        },
      },
    ],
    [venueMap],
  );
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [selectedToastGuid, setSelectedToastGuid] = useState<ToastGuid | null>(
    null,
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [toastGuidToDelete, setToastGuidToDelete] = useState<ToastGuid | null>(
    null,
  );

  const deleteMutation = useDeleteToastGuid();

  const controller = useResourceTableController<ToastGuid>({
    columnsConfig: toastGuidColumnConfigs,
    defaultSort: {
      id: 'guid',
      desc: false,
    },
    baseSearchParams: () => ({
      'filter[deletedAt]': 'null',
    }),
    buildFilterParams: (filters) =>
      buildFilterParams(filters, {
        fieldMapping: TOAST_GUID_FIELD_MAPPING,
      }),
    quickFilterFields: [
      { field: 'name', label: 'Name' },
      { field: 'guid', label: 'GUID' },
      { field: 'venue', label: 'Venue' },
    ],
    initialPageSize: 10,
    rowActions: (row) => (
      <div className="flex justify-end gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleEdit(row);
          }}
        >
          <Pencil className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteClick(row);
          }}
        >
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>
    ),
  });

  const {
    data: toastGuids,
    status,
    isError,
    error,
  } = useToastGuids({ searchParams: controller.searchParams });

  useEffect(() => {
    if (isError && error) {
      toastError(
        error instanceof Error ? error.message : 'Failed to load Toast GUIDs',
      );
    }
  }, [isError, error]);

  const rows: ToastGuid[] = toastGuids?.data ?? [];
  const meta = toastGuids?.meta;

  const handleCreate = () => {
    setSelectedToastGuid(null);
    setIsSheetOpen(true);
  };

  const handleEdit = (toastGuid: ToastGuid) => {
    setSelectedToastGuid(toastGuid);
    setIsSheetOpen(true);
  };

  const handleDeleteClick = (toastGuid: ToastGuid) => {
    setToastGuidToDelete(toastGuid);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!toastGuidToDelete) return;

    try {
      await deleteMutation.mutateAsync({ id: toastGuidToDelete.id });
      toastSuccess(`Toast GUID has been deleted.`);
      setDeleteDialogOpen(false);
      setToastGuidToDelete(null);
    } catch (err) {
      toastError(
        err instanceof Error ? err.message : 'Failed to delete Toast GUID',
      );
    }
  };

  const handleSheetSuccess = () => {
    toastSuccess(
      selectedToastGuid
        ? `Toast GUID has been updated.`
        : 'Toast GUID has been created.',
    );
  };

  return (
    <>
      <ResourceTableView<ToastGuid>
        label="Toast GUIDs"
        controller={controller}
        data={rows}
        meta={meta}
        status={status}
        summaryIcon={<Hash className="h-4 w-4 text-zinc-500" />}
        summaryLabel="Total Toast GUIDs: "
        renderExtraActions={() => (
          <Button variant="primary" onClick={handleCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Create Toast GUID
          </Button>
        )}
      />

      <ToastGuidSheet
        toastGuid={selectedToastGuid}
        isOpen={isSheetOpen}
        setIsOpen={setIsSheetOpen}
        onSuccess={handleSheetSuccess}
      />

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onSubmit={handleDeleteConfirm}
        match={toastGuidToDelete?.name ?? ''}
        title="Delete Toast GUID"
        description={`Are you sure you want to delete "${toastGuidToDelete?.name}"? This action cannot be undone.`}
        matchLabel="Type the Toast GUID name to confirm"
        submitLabel="Delete"
        isLoading={deleteMutation.isPending}
        variant="destructive"
      />
    </>
  );
}
